import { z } from 'zod'
import { Environment, IsoDateTime, IsoDuration, JsonSchemaDocument } from './common'

// The page kit's documents (entry 34): a page declares actions, the portal binds them to workflows.

export const PageAction = z.object({
  id: z.string().describe('e.g. verify_otp'),
  input: JsonSchemaDocument,
  outcomes: z.array(z.string()).min(1),
  output: JsonSchemaDocument.optional(),
  pii: z.array(z.string()).default([]),
})
export type PageAction = z.infer<typeof PageAction>

export const ManifestPage = z.object({
  id: z.string(),
  path: z.string(),
  type: z.enum(['form', 'otp', 'info', 'outcome']),
  accepts: z.array(z.string()).default([]),
  passes: z.array(z.string()).default([]),
  actions: z.array(PageAction).default([]),
  outputs: z.array(z.string()).default([]),
  required: z.boolean().default(false),
})
export type ManifestPage = z.infer<typeof ManifestPage>

/** landing.manifest.json v2 = L4 + actions. */
export const LandingManifest = z.object({
  manifest_version: z.literal(2),
  app: z.object({ name: z.string(), version: z.string() }),
  pages: z.array(ManifestPage).min(1),
})
export type LandingManifest = z.infer<typeof LandingManifest>

/** Where the landing app's code comes from: a GitHub repository we clone, or a folder / zip uploaded by hand. */
export const LandingSource = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('github'),
    repo: z.string().describe('owner/repo'),
    branch: z.string(),
  }),
  z.object({
    kind: z.literal('upload'),
    fileName: z.string().describe('the zip file or the folder name'),
    files: z.number().int().nonnegative(),
    sizeBytes: z.number().int().nonnegative(),
  }),
])
export type LandingSource = z.infer<typeof LandingSource>

export const LandingPage = z.object({
  id: z.string(),
  workspaceId: z.string(),
  slug: z.string().describe('the subdomain, e.g. moneyview-pl'),
  name: z.string(),
  lenderName: z.string(),
  product: z.string(),
  source: LandingSource,
  liveTag: z.string().nullable(),
  previewTag: z.string().nullable(),
  status: z.enum(['draft', 'preview', 'live', 'suspended']),
  manifest: LandingManifest,
  pageWorkflowId: z.string().nullable().describe('the Page workflow routing by outcome'),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
})
export type LandingPage = z.infer<typeof LandingPage>

/** Add landing page: the facts and the manifest; the page starts as a draft with no tags and no bindings. */
export const LandingPageInput = LandingPage.pick({
  slug: true,
  name: true,
  lenderName: true,
  product: true,
  source: true,
  manifest: true,
})
export type LandingPageInput = z.infer<typeof LandingPageInput>

export const BindingTemplate = z.enum(['otp_send', 'otp_verify', 'store_draft', 'pan_check', 'submit_funnel'])
export type BindingTemplate = z.infer<typeof BindingTemplate>

export const ActionBinding = z.object({
  id: z.string(),
  workspaceId: z.string(),
  landingPageId: z.string(),
  pageId: z.string(),
  actionId: z.string(),
  environment: Environment,
  workflow: z.object({ slug: z.string(), version: z.union([z.number().int(), z.literal('live')]) }),
  template: BindingTemplate.nullable(),
  inputMap: z.record(z.string(), z.string()).describe('workflow input field → JSON path in the action input'),
  outcomeMap: z.record(z.string(), z.string()).describe('workflow outcome → action outcome'),
  wait: IsoDuration,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
})
export type ActionBinding = z.infer<typeof ActionBinding>

export const ActionBindingInput = ActionBinding.omit({
  id: true,
  workspaceId: true,
  createdAt: true,
  updatedAt: true,
})
export type ActionBindingInput = z.infer<typeof ActionBindingInput>

/** The check lines of the Pages tab: every action bound, every outcome covered. */
export const BindingCheck = z.object({
  pageId: z.string(),
  actionId: z.string(),
  bound: z.boolean(),
  uncoveredOutcomes: z.array(z.string()),
  ok: z.boolean(),
})
export type BindingCheck = z.infer<typeof BindingCheck>

/** The public call the pages make: POST /public/actions/{action}. */
export const PublicActionRequest = z.object({
  siteKey: z.string(),
  visitorId: z.string(),
  landingPage: z.string(),
  page: z.string(),
  action: z.string(),
  input: z.record(z.string(), z.unknown()),
})
export type PublicActionRequest = z.infer<typeof PublicActionRequest>

export const PublicActionReply = z.object({
  run_id: z.string(),
  outcome: z.string().nullable().describe('null with status 202 when the wait ran out'),
  output: z.record(z.string(), z.unknown()).nullable(),
})
export type PublicActionReply = z.infer<typeof PublicActionReply>
